import { isApproverRole } from '@time-tracking/shared';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { useMe } from '../api/hooks.js';
import { canAccessProjects, getRoleLabel } from '../auth/access.js';
import { useAuth } from '../auth/AuthContext.js';

interface LayoutProps {
  children: ReactNode;
  pageTitle?: string;
}

const IconHelp = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconSignOut = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const IconChevron = (
  <svg
    className="um-caret"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export function Layout({ children, pageTitle }: LayoutProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const meQuery = useMe();
  const role = meQuery.data?.role;
  // Approvers/admins always see Projects; a non-approver lead sees it too (S-A).
  // Shares canAccessProjects with the route guard so the link and route agree.
  const canSeeProjects = canAccessProjects(meQuery.data);
  const utEid = meQuery.data?.utEid ?? null;
  const displayName = meQuery.data?.displayName ?? user?.name ?? user?.email ?? '';
  // The avatar shows only the server's initials (one rule, in the API). Until /v1/me
  // resolves it is an empty circle — the CSS box is fixed, so nothing shifts — rather than
  // a guess from the Cognito name that would flip once the real value lands. A failed
  // /v1/me shows '?' instead of staying blank. The avatar is aria-hidden either way; the
  // trigger's accessible name comes from displayName, not from these letters.
  const initials = meQuery.data?.initials ?? (meQuery.isError ? '?' : '');
  const roleLabel = getRoleLabel(role);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstMenuItemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (menuOpen) {
      firstMenuItemRef.current?.focus();
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    function handleMouseDown(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  function activateItem(fn: () => void) {
    setMenuOpen(false);
    triggerRef.current?.focus();
    fn();
  }

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header
        className="site-header"
        aria-label="The University of Texas at Austin – Time Tracking"
      >
        <div className="site-header__brand">
          <a
            href="https://www.utexas.edu"
            aria-label="The University of Texas at Austin"
            className="site-header__wordmark-link"
          >
            {/* The University Informal Signature—Horizontal, whose brand minimum is 25px
                (UT Standards Manual V4, p.18). 32px is a design choice with headroom, not
                the floor. The manual limits this lockup to INTERNAL communication — fine
                for a staff timesheet app, but if this ever faces the public the mark has
                to change, not just the size. Alt stays the full university name: it is
                what the mark identifies, and it contains the visible "TEXAS".
                See assets/brand/README.md before changing the size or re-sourcing. */}
            <img src="/ut-wordmark.svg" alt="The University of Texas at Austin" height={32} />
          </a>
          <div className="site-header__divider" aria-hidden="true" />
          <div className="site-header__product">
            <div className="site-header__product-name">{pageTitle ?? 'Timesheets'}</div>
            <div className="site-header__product-sub">Campus Solutions</div>
          </div>
        </div>

        <nav className="site-header__nav" aria-label="Main navigation">
          <NavLink
            to="/timesheet"
            className={({ isActive }) =>
              'site-header__nav-link' + (isActive ? ' site-header__nav-link--active' : '')
            }
          >
            My timesheet
          </NavLink>
          {/* The same approver-or-administrator set the API's requireRole('approver')
              gates /approvals on, read from its shared definition so the nav link and
              the endpoint behind it cannot disagree. `role` is undefined on first
              paint (useMe is gated on the id token), which fails closed. */}
          {role !== undefined && isApproverRole(role) && (
            <NavLink
              to="/approvals"
              className={({ isActive }) =>
                'site-header__nav-link' + (isActive ? ' site-header__nav-link--active' : '')
              }
            >
              Approvals
            </NavLink>
          )}
          {(role === 'approver' || role === 'administrator') && (
            <NavLink
              to="/analytics"
              className={({ isActive }) =>
                'site-header__nav-link' + (isActive ? ' site-header__nav-link--active' : '')
              }
            >
              Analytics
            </NavLink>
          )}
          {canSeeProjects && (
            <NavLink
              to="/projects"
              className={({ isActive }) =>
                'site-header__nav-link' + (isActive ? ' site-header__nav-link--active' : '')
              }
            >
              Projects &amp; tasks
            </NavLink>
          )}
          {role === 'administrator' && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                'site-header__nav-link' + (isActive ? ' site-header__nav-link--active' : '')
              }
            >
              Administration
            </NavLink>
          )}
          <NavLink
            to="/help"
            className={({ isActive }) =>
              'site-header__nav-link' + (isActive ? ' site-header__nav-link--active' : '')
            }
          >
            Help &amp; FAQ
          </NavLink>
        </nav>

        {user && (
          <div className="site-header__right">
            <div className="user-menu">
              <button
                ref={triggerRef}
                type="button"
                className={'user-trigger' + (menuOpen ? ' open' : '')}
                onClick={() => setMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={`${displayName} account menu`}
              >
                <span className="um-name">{displayName}</span>
                <span className="avatar" aria-hidden="true">
                  {initials}
                </span>
                {IconChevron}
              </button>
              {menuOpen && (
                <div ref={menuRef} className="user-menu-pop" role="menu" aria-label="Account">
                  <div className="um-head">
                    <span className="avatar lg" aria-hidden="true">
                      {initials}
                    </span>
                    <div className="um-id">
                      <div className="um-id-name">{displayName}</div>
                      <div className="um-id-meta">
                        {roleLabel}
                        {utEid != null ? ` · ${utEid}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="um-sep" />
                  <button
                    ref={firstMenuItemRef}
                    type="button"
                    role="menuitem"
                    className="um-item"
                    onClick={() => activateItem(() => navigate('/help'))}
                  >
                    {IconHelp}
                    <span>Help &amp; FAQ</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="um-item"
                    onClick={() => activateItem(signOut)}
                  >
                    {IconSignOut}
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      <main id="main-content" className="site-main">
        {children}
      </main>
    </>
  );
}
